import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, User, ArrowLeft, Share2, Facebook, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = "https://effendialogue-2.onrender.com";
const API = `${BACKEND_URL}/api`;

const PostPage = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/${id}`);
      setPost(response.data);
    } catch (error) {
      console.error("Error fetching post:", error);
      toast.error("Failed to fetch post");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

useEffect(() => {
  if (post?.title) {
    document.title = `Effendialogue | ${post.title}`;
  }
}, [post]);

  const sharePost = (platform) => {
    const url = window.location.href;
    const title = post?.title || "";
    
    let shareUrl = "";
    switch (platform) {
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
        break;
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      default:
        navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard!");
        return;
    }
    
    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  if (loading) {
    return (
      <div className="app-container min-h-screen">
        <div className="content-wrapper py-32 px-8 md:px-12 lg:px-20">
          <div className="animate-pulse max-w-4xl mx-auto">
            <div className="h-8 bg-gray-700 rounded mb-4 w-1/4"></div>
            <div className="h-12 bg-gray-700 rounded mb-8 w-3/4"></div>
            <div className="h-64 bg-gray-700 rounded mb-8"></div>
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-700 rounded w-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="app-container min-h-screen">
        <div className="content-wrapper py-32 px-8 md:px-12 lg:px-20 text-center">
          <h1 className="text-4xl font-bold text-red-400 mb-4">Post Not Found</h1>
          <p className="text-gray-400 mb-8">The requested post could not be found.</p>
          <Link to="/">
            <Button data-testid="back-to-home" className="bg-yellow-500 hover:bg-yellow-600 text-black">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container min-h-screen">
      <article className="content-wrapper py-32 px-8 md:px-12 lg:px-20 max-w-4xl mx-auto fade-in">
        {/* Navigation */}
        <div className="mb-8">
          <Link to="/">
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 mt-20 font-semibold">
               Read More
               <ArrowLeft className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        {/* Article Header */}
        <header className="mb-12">
          <h1 className="text-5xl font-bold mb-6 gradient-text leading-tight">
            {post.title}
          </h1>
          
          <div className="flex items-center gap-6 text-gray-400 mb-8">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5" />
              <span className="font-medium text-white">{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              <span>{formatDate(post.created_at)}</span>
            </div>
          </div>

          {/* Share Buttons */}
          <div className="flex items-center gap-4">
            <span className="text-gray-400 font-medium">Share:</span>
            <Button
              data-testid="share-twitter"
              size="sm"
              variant="ghost"
              onClick={() => sharePost("twitter")}
              className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
            >
              <Twitter className="h-4 w-4" />
            </Button>
            <Button
              data-testid="share-facebook"
              size="sm"
              variant="ghost"
              onClick={() => sharePost("facebook")}
              className="text-blue-600 hover:text-blue-500 hover:bg-blue-900/20"
            >
              <Facebook className="h-4 w-4" />
            </Button>
            <Button
              data-testid="share-copy"
              size="sm"
              variant="ghost"
              onClick={() => sharePost("copy")}
              className="text-gray-400 hover:text-gray-300 hover:bg-gray-700"
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <Separator className="mb-12 bg-gray-700" />

        {/* Featured Image */}
        {post.image_url && (
        <div className="relative w-full max-w-4xl mx-auto mb-12">
          <img
            src={post.image_url}
            alt={post.title}
            className="w-full sm:w-3/4 lg:w-1/2 mx-auto h-auto rounded-xl shadow-2xl object-cover"
          />
        </div>
        )}

        {/* Article Content */}
        <div 
          data-testid="post-content"
           className="prose prose-lg max-w-none article-content"
          style={{
            color: '#E2E8F0',
            lineHeight: '1.8'
          }}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <Separator className="my-12 bg-gray-700" />

        {/* Article Footer */}
        <footer className="text-center">
          <p className="text-gray-400 mb-6">Thank you for reading!</p>
          <Link to="/">
            <Button 
              data-testid="back-to-articles"
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-8"
            >
              Read More Articles
            </Button>
          </Link>
        </footer>
            <br></br>
    <br></br>
      </article>
    </div>
  );
};

export default PostPage;